import React, { FC, ReactNode } from "react";

import { CommonEvent } from "@narvar/nth-kit-returns-headless";
import { ErrorBanner, Language } from "../ReviewSummary/components";
import { ScrollToTop, SuccessBanner } from "../ReviewSummary/components";
import useCurrencyConfig from "../ReviewSummary/hooks/useCurrencyConfig";
import { filterItems, splitItemsForMixReturns } from "./helper";
import { useStyles } from "./indexStyles";
import { ItemSelectorRaw } from "./ItemSelectorRaw";
import type {
  ItemSelectorPayload,
  ItemSelectorProps,
  CancelReturnStatus,
} from "./types";

const Banner = ({
  type,
  children,
}: {
  type?: CancelReturnStatus["status"];
  children?: ReactNode;
}) => {
  if (type === "success")
    return (
      <ScrollToTop>
        <SuccessBanner>{children}</SuccessBanner>
      </ScrollToTop>
    );
  if (type === "error")
    return (
      <ScrollToTop>
        <ErrorBanner>{children}</ErrorBanner>
      </ScrollToTop>
    );
  return null;
};

export const ItemSelector: FC<ItemSelectorProps> = ({
  title,
  customer,
  allItems,
  lineItemGroupings,
  itemGroupingModeEnabled,
  selectedItems,
  splitSelectedItems,
  submittedReturns,
  onClick,
  onSubmit,
  onBack,
  onCancel,
  cancelReturnStatus,
  progressiveStep,
  activeStep,
  features,
  showOfflineReturns = "hidden",
  disclaimer,
}) => {
  const styles = useStyles();
  const { locale, includeCurrency } = useCurrencyConfig();
  const [eligibleItems, ineligibleItems] = filterItems(
    allItems,
    submittedReturns,
    showOfflineReturns,
  );
  const splitEligibleItems = splitSelectedItems
    ? splitItemsForMixReturns(eligibleItems, selectedItems)
    : eligibleItems;

  const handleClick: CommonEvent<ItemSelectorPayload> = (e) => {
    onClick?.(e);
  };

  return (
    <div className={styles.wrapper}>
      <ItemSelectorRaw
        {...{
          title,
          customer,
          lineItemGroupings,
          itemGroupingModeEnabled,
          eligibleItems: splitEligibleItems,
          onClick: handleClick,
          onSubmit,
          onBack,
          onCancel,
          submittedReturns,
          ineligibleItems,
          selectedItems,
          cancelReturnStatus,
          progressiveStep,
          activeStep,
          features,
          locale,
          includeCurrency,
          banner: (
            <div style={{ margin: "2rem auto" }}>
              <Banner type={cancelReturnStatus?.status}>
                <Language
                  langKey={`returnsItemListBanner${
                    cancelReturnStatus?.status ?? ""
                  }`}
                  defaultString={cancelReturnStatus?.message as string}
                />
              </Banner>
            </div>
          ),
          disclaimer,
        }}
      />
    </div>
  );
};

export { ItemSelectorRaw };
